/**
 * What a cascade PILL says about a step (owner, 2026-10-02: "pills that are very descriptive and
 * show the actual prompts"): who runs it, on which model and effort, and the words it runs on.
 *
 * The prompt is read from the flow definition the same way the work item's prompts panel reads it
 * (packages/docit-work/src/FlowPromptsPanel.tsx `promptOf`) — `prompt`, `instructions`, then the
 * tile's description, then the purpose — except that `instructions` comes BEFORE the tile's
 * description here: a generated work flow puts the executor's words in `instructions` and the
 * tile's copy (forTile: true) in `tile.description`, and a run graph shows what the executor got.
 * A person's step (a `human` node) shows its QUESTION instead. When the node has run, the prompt
 * AS SENT (the transcript's first `prompt` entry, or `meta.prompt`) is kept beside it — the
 * authored words are what the pill shows, the as-sent text is what the full view adds.
 *
 * Pure, no React: the node:assert harness runs it.
 */
import type { NodeDefinition } from "./types";

export type PillPromptKind = "prompt" | "question" | "none";

export interface PillPrompt {
  kind: PillPromptKind;
  /** The authored words (or the question). Empty when the definition carries none. */
  text: string;
  /** The prompt as the executor actually received it, when the run recorded it. */
  asSent?: string;
  /** A person's step: the answer choices, when the question offers them. */
  options?: string[];
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** The step's words: a person's question, else the executor's prompt, else nothing. */
export function pillPromptOf(
  node: NodeDefinition | undefined,
  asSent?: string | null,
): PillPrompt {
  const sent = str(asSent) || undefined;
  if (!node) {
    return sent ? { kind: "prompt", text: sent, asSent: sent } : { kind: "none", text: "" };
  }
  const human = node.human && typeof node.human === "object" ? node.human : undefined;
  if (node.kind === "human" || human) {
    const title = str(human?.title);
    const text = str(human?.text);
    const question =
      title && text && !text.startsWith(title) ? `${title}\n\n${text}` : text || title;
    const options = Array.isArray(human?.options)
      ? human!.options
          .map((o) => (o && typeof o === "object" ? str((o as { label?: unknown }).label) : ""))
          .filter((l) => l.length > 0)
      : undefined;
    if (question) {
      return {
        kind: "question",
        text: question,
        ...(options && options.length ? { options } : {}),
        ...(sent ? { asSent: sent } : {}),
      };
    }
  }
  const tile = node.tile && typeof node.tile === "object" ? node.tile : undefined;
  const authored =
    str(node.prompt) ||
    str(node.instructions) ||
    str(tile?.description) ||
    str(node.description) ||
    str(node.purpose);
  if (authored) {
    return { kind: "prompt", text: authored, ...(sent ? { asSent: sent } : {}) };
  }
  return sent ? { kind: "prompt", text: sent, asSent: sent } : { kind: "none", text: "" };
}

/** The first `prompt` entry of a node's transcript — the words as they were sent. */
export function promptAsSent(
  trace: ReadonlyArray<{ kind: string; text: string }> | undefined,
  meta?: { prompt?: unknown } | null,
): string | undefined {
  const fromMeta = str(meta?.prompt);
  if (fromMeta) return fromMeta;
  const entry = trace?.find((e) => e.kind === "prompt");
  return entry ? str(entry.text) || undefined : undefined;
}

/** Who runs the step, in words a person reads: "IDE agent", "AI thinker", "Person", … */
export interface PillExecutor {
  label: string;
  /** The role the step is assigned to ("dev", "qa", "reviewer"), when it names one. */
  role?: string;
  /** Longer description for the tooltip (the executor's own label, the thinker id). */
  title?: string;
}

const KIND_LABEL: Record<string, string> = {
  compute: "Transform (no model)",
  work: "Work operation",
  fork: "Fork (parallel)",
  join: "Join (barrier)",
  foreach: "For each",
  forEach: "For each",
  subflow: "Subflow",
  mechanics: "Mechanics",
};

export function pillExecutorOf(
  node: NodeDefinition | undefined,
  executorKind?: string | null,
  thinkerId?: string | null,
): PillExecutor {
  const exec =
    node && node.executor && typeof node.executor === "object"
      ? (node.executor as { kind?: unknown; label?: unknown; role?: unknown; thinkerId?: unknown })
      : undefined;
  const role = str(node?.role) || str(exec?.role) || undefined;
  const ownerRole = str(node?.ownerRole);
  const thinker = str(thinkerId) || str(exec?.thinkerId) || ownerRole;
  const titleParts = [str(exec?.label), thinker].filter((s) => s.length > 0);
  const title = titleParts.length ? titleParts.join(" · ") : undefined;
  const done = (label: string): PillExecutor => ({
    label,
    ...(role ? { role } : {}),
    ...(title ? { title } : {}),
  });

  const ek = str(exec?.kind).toLowerCase();
  if (ek === "ide") return done("IDE agent");
  if (ek === "ai") return done("AI thinker");
  if (ek === "human") return done("Person");

  const kind = str(node?.kind) || str(executorKind);
  if (kind === "human" || executorKind === "human") return done("Person");
  if (KIND_LABEL[kind]) return done(KIND_LABEL[kind]);
  if (executorKind && KIND_LABEL[executorKind]) return done(KIND_LABEL[executorKind]);
  // A thinker: the IDE window's Claude when its thinker/owner role says so, else a server model.
  if (/^ide[:\-]|^claude-code|vscode/i.test(thinker)) return done("IDE agent");
  return done("AI thinker");
}

/**
 * A transition condition as an edge label a person reads: `always` says nothing and is dropped;
 * `contains(prev.result, "VERDICT: ITERATE")` reads as `VERDICT: ITERATE`; anything else is kept,
 * capped at 28 characters so it fits the cascade's column gap (the full text is the tooltip).
 */
export function shortCondition(condition: unknown): string {
  const c = str(condition);
  if (!c || c.toLowerCase() === "always") return "";
  const m = /^(?:contains|startsWith|endsWith|matches)\(\s*prev\.result\s*,\s*["'](.+)["']\s*\)$/.exec(c);
  const s = m ? m[1] : c;
  return s.length > 28 ? s.slice(0, 27) + "…" : s;
}

/** "claude-sonnet-5" → "sonnet-5"; the effort rides after a dot when known. */
export function pillModelLine(model?: string | null, effort?: string | null): string {
  const m = str(model).replace(/^claude-/, "");
  const e = str(effort);
  if (m && e) return `${m} · ${e} effort`;
  if (m) return m;
  if (e) return `${e} effort`;
  return "";
}
